/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 내비게이션 항목 클래스를 정의한다.
 *
 * @file /src/components/navigation/elements/Page.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 29.
 */
import * as parent from './Item';
import { Navigation } from '@components/navigation/Navigation';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Format } from '@utils/Format';

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 항목아이콘
     */
    icon: string;

    /**
     * 경로
     */
    path: string;
}

export class Page extends parent.Item {
    /**
     * 엘리먼트 타입
     */
    type = 'navigationitem';

    /**
     * 항목아이콘
     */
    icon: string;

    /**
     * 경로
     */
    path: string;

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 페이지항목 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Page> = null) {
        super(properties);

        this.icon = this.properties.icon;
        this.path = properties.path;
    }

    /**
     * 아이콘을 가져온다.
     *
     * @returns 아이콘 DOM
     */
    $getIcon(): Dom {
        if (this.$icon === null) {
            this.$icon = Html.create('i');
            this.$icon.addClass(...this.icon.split(' '));
        }

        return this.$icon;
    }

    /**
     * 부모 컴포넌트를 가져온다.
     *
     * @returns 부모 컴포넌트
     */
    getComponent(): Navigation {
        return this.component as Navigation;
    }

    /**
     * 내비게이션 항목을 검색한다.
     *
     * @param keyword - 키워드
     * @returns 매칭여부
     */
    search(keyword: string): boolean {
        keyword = (keyword ?? '').trim();

        if (keyword.length == 0) {
            this.$element.removeClass('unmatched');
            return true;
        }

        let keycode = this.$element.getData('keycode');
        if (keycode === null) {
            keycode = Format.keycode(this.title);
            this.$element.setData('keycode', keycode, false);
        }

        if (keycode.includes(keyword) == true) {
            this.$element.removeClass('unmatched');
            return true;
        } else {
            this.$element.addClass('unmatched');
            return false;
        }
    }

    /**
     * 앨리먼트를 랜더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        this.$element.setData('path', this.path, false);
        this.$element.setAttr('data-variant', 'page');

        const url = this.getComponent().getPath(this.path);
        const $a = Html.create('a', { href: url });
        $a.setAttr('draggable', 'false');
        $a.append(this.$getIcon());
        $a.append(this.$getTitle());
        $a.append(this.$getSorter());

        $a.on('click', async ($a, e) => {
            e.preventDefault();
            if (this.getComponent().$getContainer().hasClass('config') === true) {
                return;
            }

            await this.getComponent()
                .getViewport()
                .open($a.getAttr('href'), e.button === 0 ? 'self' : 'blank');
        });

        /*
        if (this.getComponent().getPanelLoader() !== null) {
            $a.on('click', async ($a, e) => {
                e.preventDefault();
                if (this.getComponent().$getContainer().hasClass('config') === true) return;

                if (e.button == 0) {
                    e.preventDefault();
                    const panel = await this.getComponent().getPanelLoader()($a.getAttr('href'));
                    if (typeof panel == 'boolean') {
                        if (panel === true) {
                            location.replace(url);
                        } else {
                            this.getComponent().getViewport().dekstopActive();
                        }
                    } else {
                        e.preventDefault();
                        await this.getComponent().getViewport().active(panel, url);
                    }
                }
            });
        }
        */

        this.$getElement().append($a);
        if (url === this.getComponent().contextPath) {
            this.$getElement().addClass('selected');
        }

        await this.onRender();
    }
}
